export type BrokerGroupColorKey =
  | "white"
  | "teal"
  | "light-teal"
  | "lime"
  | "yellow"
  | "gold";

export interface BrokerGroupColor {
  key: BrokerGroupColorKey | "custom";
  label: string;
  hex: string;
  /** Background tint (low opacity) for row/card body. */
  tintBg: string;
  /** Border color style for the accent stripe. */
  border: string;
}

/** Legacy preset swatches (kept as quick picks). */
export const BROKER_GROUP_COLORS: BrokerGroupColor[] = [
  { key: "white",      label: "White",      hex: "#FFFFFF", tintBg: "transparent",              border: "hsl(var(--border))" },
  { key: "teal",       label: "Teal",       hex: "#02857E", tintBg: "rgba(2, 133, 126, 0.08)",  border: "#02857E" },
  { key: "light-teal", label: "Light teal", hex: "#1AB7AA", tintBg: "rgba(26, 183, 170, 0.10)", border: "#1AB7AA" },
  { key: "lime",       label: "Lime",       hex: "#C4E84A", tintBg: "rgba(196, 232, 74, 0.14)", border: "#C4E84A" },
  { key: "yellow",     label: "Yellow",     hex: "#F7CF0D", tintBg: "rgba(247, 207, 13, 0.12)", border: "#F7CF0D" },
  { key: "gold",       label: "Gold",       hex: "#B8960A", tintBg: "rgba(184, 150, 10, 0.12)", border: "#B8960A" },
];

const KEY_MAP = new Map(BROKER_GROUP_COLORS.map((c) => [c.key, c]));

/** Default color for brand-new groups — white / no tint. */
export const DEFAULT_GROUP_COLOR_HEX = "#FFFFFF";

function normalizeHex(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (/^#([0-9a-f]{6})$/i.test(s)) return s.toUpperCase();
  if (/^#([0-9a-f]{3})$/i.test(s)) {
    const [, r, g, b] = s.match(/^#(.)(.)(.)/i)!;
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return null;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/**
 * Resolve any stored color value (legacy preset key OR hex) to a display color.
 * Falls back to WHITE (no tint) when null/invalid.
 */
export function getBrokerGroupColor(value: string | null | undefined): BrokerGroupColor {
  if (value && KEY_MAP.has(value as BrokerGroupColorKey)) return KEY_MAP.get(value as BrokerGroupColorKey)!;
  const hex = value ? normalizeHex(value) : null;
  if (!hex) return KEY_MAP.get("white")!;
  if (hex === "#FFFFFF") return KEY_MAP.get("white")!;
  const [r, g, b] = hexToRgb(hex);
  return {
    key: "custom",
    label: "Custom",
    hex,
    tintBg: `rgba(${r}, ${g}, ${b}, 0.12)`,
    border: hex,
  };
}
